import { type NumericInput, getErrorMessage, parseCurrency, readProperty } from '@/lib/utils'
import type { PosCartOrderChange } from '@/lib/services/carts/carts.types'
import type { LineItemVariant } from '@/lib/services/line-items/line-items.types'

/** The catalog-variant fields the POS stock and price helpers read. Prices are API money. */
export interface PosInventoryVariant {
  id?: number | null
  price?: NumericInput
  effective_price?: NumericInput
  /** The variant's active PriceAdjustment; only its `id` is read. */
  active_price_adjustment?: unknown
  use_inventory?: boolean | null
  on_hand?: number | string | null
  in_stock?: boolean | null
}

type MaybePosInventoryVariant = PosInventoryVariant | null | undefined

/** The POS cart-line fields the stock and price helpers read. */
export interface PosInventoryCartItem {
  variant_id?: number | null
  quantity?: number | string | null
  /** Unit price as a decimal string. */
  price?: NumericInput
  variant?: { id?: number | null; active_price_adjustment?: unknown } | null
  /** The line's LineItemSnapshot; only `price_adjustment_id` is read. */
  variant_details?: Pick<LineItemVariant, 'price_adjustment_id'> | null
}

type MaybePosInventoryCartItem = PosInventoryCartItem | null | undefined

/** Quantity in the cart, keyed by variant id. */
export type PosQuantityByVariantId = Record<string, number>

export interface PosInventoryProduct {
  variants?: MaybePosInventoryVariant[] | null
}

function normalizeTrackedOnHand(variant: MaybePosInventoryVariant): number | null {
  if (!variant?.use_inventory || variant?.on_hand === null || variant?.on_hand === '') {
    return null
  }

  const onHand = Number(variant.on_hand)

  if (!Number.isFinite(onHand)) {
    return null
  }

  return Math.max(onHand, 0)
}

export function formatPosUnitPrice(price: NumericInput): string {
  return parseCurrency(price).toFixed(2)
}

export function getPosVariantUnitPrice(variant: MaybePosInventoryVariant): string {
  return formatPosUnitPrice(variant?.effective_price ?? variant?.price)
}

function normalizePriceAdjustmentId(value: unknown): number | null {
  const priceAdjustmentId = Number(value)

  return Number.isInteger(priceAdjustmentId) && priceAdjustmentId > 0 ? priceAdjustmentId : null
}

function getPosVariantPriceAdjustmentId(variant: MaybePosInventoryVariant): number | null {
  return normalizePriceAdjustmentId(readProperty(variant?.active_price_adjustment, 'id'))
}

function getPosCartItemPriceAdjustmentId(item: MaybePosInventoryCartItem): number | null {
  return normalizePriceAdjustmentId(
    item?.variant_details?.price_adjustment_id ??
      readProperty(item?.variant?.active_price_adjustment, 'id')
  )
}

export function hasMatchingPosCartItem(
  item: MaybePosInventoryCartItem,
  variant: MaybePosInventoryVariant
): boolean {
  return (
    item?.variant_id === variant?.id &&
    formatPosUnitPrice(item?.price) === getPosVariantUnitPrice(variant) &&
    getPosCartItemPriceAdjustmentId(item) === getPosVariantPriceAdjustmentId(variant)
  )
}

export function getPosVariantOnHand(variant: MaybePosInventoryVariant): number | null {
  return normalizeTrackedOnHand(variant)
}

function firstValidationError(errors: unknown): string | null {
  if (typeof errors === 'string' && errors.trim()) {
    return errors
  }

  if (Array.isArray(errors)) {
    const items: unknown[] = errors

    for (const error of items) {
      const message = firstValidationError(error)
      if (message) {
        return message
      }
    }

    return null
  }

  if (typeof errors === 'object' && errors !== null) {
    const values: unknown[] = Object.values(errors)

    for (const value of values) {
      const message = firstValidationError(value)
      if (message) {
        return message
      }
    }
  }

  return null
}

/**
 * @param heldUnits Units the draft keeps without the shelf (its order took them from stock, or sold them before the
 * variant tracked stock): they are not asked of the shelf again, so they come off what the cart counts against it.
 */
export function getPosCartQuantityByVariantId(
  cartItems: MaybePosInventoryCartItem[] | null | undefined = [],
  heldUnits: PosCartOrderChange['held_units'] | null | undefined = []
): PosQuantityByVariantId {
  const quantityByVariantId = (cartItems || []).reduce<PosQuantityByVariantId>(
    (quantities, item) => {
      const variantId = Number(item?.variant_id ?? item?.variant?.id ?? 0)

      if (!variantId) {
        return quantities
      }

      const quantity = Number(item?.quantity ?? 0)

      quantities[variantId] = (quantities[variantId] ?? 0) + Math.max(quantity, 0)

      return quantities
    },
    {}
  )

  for (const { variant_id: variantId, units } of heldUnits ?? []) {
    quantityByVariantId[variantId] = (quantityByVariantId[variantId] ?? 0) - Math.max(units, 0)
  }

  return quantityByVariantId
}

export function getRemainingVariantInventory(
  variant: MaybePosInventoryVariant,
  quantityByVariantId: PosQuantityByVariantId | null | undefined = {}
): number | null {
  const onHand = getPosVariantOnHand(variant)

  if (onHand === null) {
    return null
  }

  // String() is the property key JavaScript would use for the id anyway.
  const inCartQuantity = Number(quantityByVariantId?.[String(variant?.id)] ?? 0)

  return Math.max(onHand - inCartQuantity, 0)
}

export function isPosVariantOutOfStock(
  variant: MaybePosInventoryVariant,
  quantityByVariantId: PosQuantityByVariantId | null | undefined = {}
): boolean {
  if (!variant) {
    return true
  }

  if (!variant.use_inventory) {
    return false
  }

  const remainingInventory = getRemainingVariantInventory(variant, quantityByVariantId)

  if (remainingInventory === null) {
    return variant.in_stock === false
  }

  return remainingInventory < 1
}

export function isPosProductOutOfStock(
  product: PosInventoryProduct | null | undefined,
  quantityByVariantId: PosQuantityByVariantId | null | undefined = {}
): boolean {
  const variants = Array.isArray(product?.variants) ? product.variants : []

  if (!variants.length) {
    return true
  }

  return variants.every((variant) => isPosVariantOutOfStock(variant, quantityByVariantId))
}

export function getPosInventoryErrorMessage(error: unknown): string | undefined {
  const validationMessage = firstValidationError(
    readProperty(readProperty(readProperty(error, 'response'), 'data'), 'errors')
  )

  if (validationMessage) {
    return validationMessage
  }

  return getErrorMessage(error)
}
